import axios from 'axios';
import utils from '../utils';
import cache from './cache';
import md5 from 'md5';
import jsonpAdapter from 'axios-jsonp';

cache.queries = {};

// limit how many queries we cache, the results can add up in memory
const CACHE_LIMIT = 10;
const RAPID_QUERY_WINDOW = 1000;
const RAPID_QUERY_LIMIT = 15;
const FILTER_LIMIT = 20;

// Define what SOLR API fields are to be treated as arrays
const SOLR_ARRAY_FIELDS = ['fq', 'bq', 'facet.field'];

class Solr {
	constructor(solrConfig, searchConfig) {
		// timestamp of last query
		this.lastQueryTS = 0;
		// count of querys in rapid window
		this.rapidQueryCount = 0;


		this.queryFilters = [];
		let defaultConfig = {
			highlight: false,
			spellcheck: false,
			solrRoot: searchConfig.solrRoot,
			solrPath: 'browse2',
			siteLimit: true,
			advanced: false,
			query: {},
		};
		this.searchConfig = searchConfig;
		this.solrConfig = Object.assign(defaultConfig, solrConfig);
		this.coreFields = {
			id: 'id',
			url: 'url',
			title: 'title',
			description: 'description',
		};
		// get fields solrConfig
		this.fields = Object.assign(this.coreFields, solrConfig && solrConfig.fields);

		// get default query
		// hl=on&df=strippedContent,description&hl.simple.pre=<b>&hl.simple.post=</b>&hl.defaultSummary=true&hl.snippets=1&hl.fragsize=300&hl.fl=strippedContent,description&affiliate=cdc-main
		let defaultQuery = {
			q: '',
			fq: [],
			wt: 'json',
			start: 0,
			rows: searchConfig.pageSize,
			fl: [],
		};
		// defaults for highlighting
		if (this.solrConfig.highlight) {
			defaultQuery.hl = 'on';
			defaultQuery['hl.simple.pre'] = '<b>';
			defaultQuery['hl.simple.post'] = '</b>';
			defaultQuery['hl.defaultSummary'] = true;
			defaultQuery['hl.snippets'] = 1;
			defaultQuery['hl.method'] = 'unified';
			defaultQuery['hl.fragsize'] = 300;
		}
		// Add query filters
		if (this.solrConfig.queryFilter) {
			this.addQueryFilter(this.solrConfig.queryFilter);
		}
		//Add Facets
		if (this.solrConfig.faceted) {
			let filterFacets = this.solrConfig.facets
			if (filterFacets && filterFacets.length) {
				defaultQuery.facet = 'on';
				defaultQuery['facet.mincount'] = 1;
				defaultQuery['facet.field'] = filterFacets;
			}
		}
		this.queryBase = defaultQuery;
		if (this.solrConfig.query) {
			this.queryBase = this._mergeQueries(defaultQuery, this.solrConfig.query);
		}
		// add any missing fields to fl
		let fls = utils.objectValues(this.fields);
		fls.forEach((fl) => {
			if (-1 === this.queryBase.fl.indexOf(fl)) {
				this.queryBase.fl.push(fl);
			}
		});
		this.searchParts = {}; // to track current search
	}

	getSearchParts(data) {
		data = Object.assign({}, data);

		data.date1 = data.date1 ? utils.toDate(data.date1) : null;
		data.date2 = data.date2 ? utils.toDate(data.date2) : null;

		// test if dates are valid
		var parts = {
			any: utils.getWords(data.any),
			all: utils.getWords(data.all),
			exact: [utils.getWords(data.exact).join(' ')],
			none: utils.getWords(data.none),
			query: utils.getWords(data.query),
			boost: [],
			url: utils.getWords(data.url),
			language: data.language ? utils.cleanString(data.language) : null,
			date1: null,
			date2: null,
			page: data.page ? parseInt(data.page, 10) : 1,
			siteLimit: data.siteLimit,
		};

		if (isNaN(parts.page) || 1 > parts.page) {
			parts.page = 1;
		}

		// All doesn't include query or exact
		parts.all = utils.arrayDiff(parts.all, parts.query);
		parts.all = utils.arrayDiff(parts.all, parts.exact);

		// Drop words from all, query, and exact from any
		if (parts.any.length) {
			parts.any = utils.arrayDiff(parts.any, parts.query);
			parts.any = utils.arrayDiff(parts.any, parts.all);
			parts.any = utils.arrayDiff(parts.any, parts.exact);
		}

		if ((data.date1 || data.date2) && this.solrConfig.advanced && this.fields.date) {
			// if date1 is after date2, switch
			if (data.date1 && data.date2 && data.date1.getTime() > data.date2.getTime()) {
				data.date1 = new Date(data.date2);
				data.date2 = new Date(data.date1);
			}
			if (data.date1) {
				parts.date1 =
					data.date1.getFullYear() +
					'-' +
					utils.pad(data.date1.getMonth() + 1, 2) +
					'-' +
					utils.pad(data.date1.getDate()) +
					'T00:00:00.000Z';
			} else {
				parts.date1 = '*';
			}
			if (data.date2) {
				parts.date2 =
					data.date2.getFullYear() +
					'-' +
					utils.pad(data.date2.getMonth() + 1, 2) +
					'-' +
					utils.pad(data.date2.getDate()) +
					'T23:59:59.999Z';
			} else {
				parts.date2 = '*';
			}
		}
		return parts;
	}

	/**
	 * Read query params and build a SOLR query term
	 * @param object  override     Any search form values we want to override, if any
	 */
	buildQuery(data) {
		var parts = (this.searchParts = this.getSearchParts(data));
		var terms = [];
		var query = {
			q: '',
			fq: [],
		};

		// when not debugging, limit response
		if (!this.searchConfig.debug) {
			query.echoParams = 'none';
			query.indent = 'false';
		}

		if (parts.query.length) {
			terms.push(parts.query.join(' '));
		}

		if (this.solrConfig.advanced) {
			if (parts.exact.length) {
				parts.exact.forEach(function (exact) {
					if (exact) {
						terms.push('"' + exact + '"');
					}
				});
			}

			if (parts.all.length) {
				parts.all.forEach((term) => {
					terms.push('(' + term + ')');
				});
			}
			// Drop words from all, query, and exact from any
			if (parts.any.length) {
				if (!terms.length) {
					terms.push('""');
				}
				terms.push('(' + parts.any.join(' ') + ')');
			}

			if (parts.none.length) {
				terms.push('-(' + parts.none.join(' ') + ')');
			}
		}

		terms = utils.unique(terms);
		query.q = encodeURI(terms.join(' AND ').trim());
		if (this.solrConfig.advanced) {
			if (parts.language && this.fields.language) {
				query.fq.push(this.fields.language + ':' + parts.language);
			}

			if (parts.date1 && parts.date2 && this.fields.date) {
				query.fq.push(this.fields.date + ':[' + parts.date1 + ' TO ' + parts.date2 + ']');
			}
			if (parts.url.length && this.solrConfig.advanced) {
				query.fq.push('(url:"' + parts.url.join('" OR url:"').trim() + '")');
			}
			if (parts.siteLimit && 'cdc' !== parts.siteLimit) {
				// sitelimit logic
				let siteLimit = utils.cleanTerm(parts.siteLimit);
				let isCoronaVirus =
					-1 < siteLimit.indexOf('coronavirus') || -1 > siteLimit.indexOf('covid-data-tracker');
				let siteLimitParts = [];

				if (this.solrConfig.siteLimit) {
					if (isCoronaVirus) {
						// we scan multiple locations for Covid local search
						siteLimit =
							'coronavirus/2019-nCoV|covid-data-tracker|vaccines/covid-19|vaers.hhs.gov|vaccines.gov|mis';
						// for covid, if this is a Best Bet query, add 'media' folder
						if (this.solrConfig.bestBets) {
							if ('string' === typeof this.searchConfig.coronavirusBB) {
								siteLimit +=
									'|' + utils.cleanString(this.searchConfig.coronavirusBB).replace(/^\|/, '');
							}
						}
					}

					String(siteLimit)
						.split(/\s*\|\s*/)
						.forEach((limit) => {
							if (-1 === limit.indexOf('.')) {
								limit = 'cdc.gov/' + limit;
							}
							siteLimitParts.push(this.fields.url + ':"' + utils.cleanTerm(limit) + '"');
						});

					// SOLR expected format for local search
					// fq=(url:"www.cdc.gov/niosh" OR url:"blogs.cdc.gov/niosh-science-blog")
					query.fq.push('(' + siteLimitParts.join(' OR ') + ')');
				} else if (isCoronaVirus) {
					query.q += ' AND ("covid" OR "coronavirus")';
				} else {
					query.q += ' AND ' + utils.cleanTerm(siteLimit);
				}
			}
		}
		if (this.solrConfig.faceted) {
			const { checkedFilters, topic, audience } = data;

			const applyFilter = (fieldName, values, queryKey) => {
				if (values && values.length) {
					const queryValues = values.map(value => `"${value}"`).join(" AND ");
					query.fq.push(`${queryKey}:(${queryValues})`);
				}
			};

			// Sync checkedFilters and data.topic/audience
			const filtersToApply = {
				topic: checkedFilters?.topic || topic,
				audience: checkedFilters?.audience || audience
			};

			// Apply filters for topic and audience
			applyFilter('topic', filtersToApply.topic, 'topic_ss');
			applyFilter('audience', filtersToApply.audience, 'audience_ss');
		}
		if ('number' === typeof parts.page) {
			query.start = (parts.page - 1) * this.searchConfig.pageSize;
		}

		return query;
	}

	// combines 2 Solr querys
	_mergeQueries(a, b) {
		var queries = [a, b];
		var newQuery = { fq: [] };
		if ('object' !== typeof b) {
			return a;
		}
		if ('object' !== typeof a) {
			return b;
		}
		queries.forEach((query) => {
			for (let key in query) {
				let value = query[key];
				if (!query.hasOwnProperty(key)) {
					continue;
				}
				// Array fields are merged, otherwise treat as a string
				if (-1 < SOLR_ARRAY_FIELDS.indexOf(key)) {
					if (!newQuery[key]) {
						newQuery[key] = [];
					}
					if (!Array.isArray(newQuery[key])) {
						newQuery[key] = utils.toArray(newQuery[key]);
					}
					newQuery[key] = newQuery[key].concat(value);
				} else {
					newQuery[key] = value;
				}
			}
		});
		return newQuery;
	}

	/**
	 * Run actual Solr query against form data
	 * @param {*} data
	 */
	query(data, override) {
		var formData = Object.assign({}, data, override);
		var solrUrl = this.solrConfig.solrRoot.replace(/\/$/, '') + '/' + this.solrConfig.solrPath.replace(/^\//, '');
		if (this.solrConfig.solrUrl) {
			solrUrl = this.solrConfig.solrUrl;
		}
		var formQuery = this.buildQuery(formData);
		var query = this._mergeQueries(this.queryBase, formQuery);

		// apply any queryFilters
		query = this._applyQueryFilters(query, formData);

		// At this point, query is an object that closely resembles the SOLR request, so flatten and make the request
		return new Promise((resolve, reject) => {

			let ts = (+ new Date());
			if (RAPID_QUERY_WINDOW > (ts - this.lastQueryTS)) {
				this.rapidQueryCount++;
			} else {
				this.rapidQueryCount = 0;
			}
			this.lastQueryTS = ts;
			if (RAPID_QUERY_LIMIT <= this.rapidQueryCount) {
				return reject('Rate limit reached');
			}

			var parts = [];
			// Up front, q always goes first
			if (query.q) {
				parts.push('q=' + utils.maybeJoin(query.q, '').trim());
				delete query.q;
			}
			// Now do the remaining parameters
			for (let key in query) {
				let value = query[key];
				if (-1 < SOLR_ARRAY_FIELDS.indexOf(key)) {
					if (!Array.isArray(value)) {
						value = utils.toArray(value);
					}
					value.forEach((elValue) => {
						parts.push(key + '=' + String(elValue).trim());
					});
				} else if ('fl' === key) {
					parts.push('fl=' + utils.maybeJoin(value, ',').trim());
				} else {
					parts.push(key + '=' + utils.maybeJoin(value, '').trim());
				}
			}
			let solrRequestUrl = solrUrl + '?' + parts.join('&');
			let hash = md5(solrRequestUrl);
			let results = this._getCache(hash);
			if (results) {
				this.searchConfig.debug && console.debug('Cached:', hash, solrRequestUrl);
				return resolve(results);
			}

			this.searchConfig.debug && console.info('SORL query: ', solrRequestUrl);
			return this.runQuery(solrRequestUrl)
				.then((response) => {
					let builtResults = this._buildResponse(response);
					this._setCache(hash, builtResults);
					resolve(builtResults);
				})
				.catch((error) =>
					resolve({
						total: 0,
						results: [],
						error,
					})
				);
		});
	}

	runQuery(solrRequestUrl) {
		// use JSONP if running from proxy
		//console.log('request url', solrRequestUrl);
		if (solrRequestUrl.match(/msappproxy\.net/) || this.solrConfig.crossdomain) {
			return axios({
				url: solrRequestUrl,
				adapter: jsonpAdapter,
				callbackParamName: 'json.wrf',
			});
		}

		// non JSONP response
		return axios({
			url: solrRequestUrl,
		});
	}

	addQueryFilter(filterFunction) {
		if ('function' === typeof filterFunction) {
			this.queryFilters.push(filterFunction);
		}
	}

	_applyQueryFilters(query, formData) {
		this.queryFilters.forEach((queryFilter) => {
			query = queryFilter(query, this.searchParts, formData);
		});
		return query;
	}

	// Function to add unique values to an array
	uniqueFiltersArray(array) {
		return array.filter((item, index, self) => {
			return typeof item === 'string' && self.indexOf(item) === index;
		});
	}

	getFacetCounts(solrTerms) {
		const result = {};
		for (let i = 0; i < solrTerms.length; i += 2) {
			const id = solrTerms[i];
			const count = solrTerms[i + 1];
			result[id] = count;
		}
		return result;
	}

	// Compiles structured response from raw SOLR response
	_buildResponse(response) {
		var results = [];
		let filters = {
			topic:[],
			audience: []
		};
		let counts = {};
		let data = (response && response.data && response.data) || {};
		var rawResults = (data.response && data.response.docs) || [];
		var total = parseInt((data.response && data.response.numFound) || 0);
		var highlights = Object.assign({}, data.highlighting);
		if (!Array.isArray(rawResults)) {
			return { total, results };
		}
		// Add Prop to Data Query to Exclude any other Tab for building Filter Results
		if ((this.solrConfig.faceted) && data.facet_counts?.facet_fields) {
			const facets = data.facet_counts.facet_fields;

			if (facets.audience_ss) {
				counts.audience = this.getFacetCounts(facets.audience_ss);
				filters.audience = this.uniqueFiltersArray(facets.audience_ss);
			}

			if (facets.topic_ss) {
				counts.topic = this.getFacetCounts(facets.topic_ss);
				filters.topic = this.uniqueFiltersArray(facets.topic_ss).slice(0, FILTER_LIMIT);
			}
		}
		rawResults.forEach((rawResult, i) => {
			var result = {};
			var id;
			for (let field in this.fields) {
				let value = rawResult[this.fields[field]] || '';
				if (this.coreFields[field] || (Array.isArray(value) && 2 > value.length)) {
					value = utils.cleanString(utils.arrayFirst(value));
				}
				if (typeof value === 'string' && /<B>Error processing SSI file<\/B>/gi.test(value)) {
					value = ''; // skip this result
				}
				result[field] = value;
			}
			id = result.id || '';

			// if highlighting is turned on, compile description
			if (this.solrConfig.highlight && highlights && highlights[id]) {
				let descriptions = [];
				if (result.description) {
					descriptions.push(result.description);
				}
				utils.objectValues(highlights[id]).forEach((values) => {
					descriptions = descriptions.concat(values);
				});

				// clean up descriptions
				if (this.solrConfig.cleanDescription) {
					for (let dIndex in descriptions) {
						descriptions[dIndex] = this.solrConfig.cleanDescription(descriptions[dIndex]);
					}
				}

				// sort by presence of bold tags
				descriptions.sort((a, b) => {
					let weights = [this._getDescriptionWeight(a), this._getDescriptionWeight(b)];
					if (weights[0] === weights[1]) {
						return a.length > b.length ? -1 : 1;
					}
					return weights[0] > weights[1] ? -1 : 1;
				});
				if (descriptions.length) {
					result.description = descriptions.shift().trim();
					if (this.searchConfig.debug) {
						result.description += ' (' + this._getDescriptionWeight(result.description) + ')';
						result.altDescriptions = [];
						descriptions.forEach((altDescription) => {
							result.altDescriptions.push(
								altDescription + ' (' + this._getDescriptionWeight(altDescription) + ')'
							);
						});
					}
				}
				// clean final description
				result.description = result.description.replace(/[_\-+\.]{5,99}/g, '').replace(/\s\s+/g, ' ');
			}
			if (result.description) {
				result.fullDescription = result.description;
				// need to clean HTML as it'll be embedded in a div with <b> tags
				result.description = utils.cleanHTML(result.description);

				//remove ssi error message from description
				const regex = /<\/?b>/g;
				const errorRegex = /Error processing SSI file/gi;
				const trailing = /^\.\s*|\s*\.$|(?<=\.|\s)(?=\s*$)/gi;
				let temp = result.description.replace(regex, '');
				if (errorRegex.test(temp)) {
					temp = temp.replace(errorRegex, '').trim();
					temp = temp.replace(trailing, '')
					result.description = temp;
				}

				result.description = utils.truncateString(result.description, this.searchConfig.limits.description);
				result.description = utils.cleanOutputString(result.description);
			}

			if (!result.title) {
				result.title = this._getTitle(result);
			}
			result.title = utils.stripTags(result.title, ['<b>']);
			if (!this.searchConfig.intranet) {
				// remove tail " | CDC" or "| cdc.gov" on Internet search;
				result.title = String(result.title)
					.trim()
					.replace(/(\s+[\|-]\s+[a-z\.]+)+$/i, '');
			}
			result.title = utils.truncateString(result.title, this.searchConfig.limits.title);
			// clean up trailing broken html tags
			result.title = result.title.replace(/\s*<\/?\w*$/i, '');
			result.title = utils.cleanOutputString(result.title);

			// apply resultFilter
			if ('function' === typeof this.solrConfig.resultFilter) {
				result = this.solrConfig.resultFilter(result);
			}

			// clean prohibited domains
			if (result.url) {
				result.url = String(result.url).replace(/^(https?:)?(\/\/(beta|dfe-stage)\.cdc\.gov)/i,'https://www.cdc.gov');
				if ('object' === typeof this.searchConfig.hostMap) {
					Object.entries(this.searchConfig.hostMap).forEach(([from, to]) => {
						if (from && to) {
							result.url = result.url.replace(new RegExp(`//${from}/`, 'i'), `//${to}/`);
						}
					})
				}
			}
			results.push(result);
		});
		// apply resultsFilter
		if ('function' === typeof this.solrConfig.resultsFilter) {
			results = this.solrConfig.resultsFilter(results, response);
			total = results.length;
		}
		// ADD FACETS and COUNTS to solr query
		let responseObj = this.solrConfig.faceted ? { total, results, filters, counts } : { total, results };

		// if Spellcheck is enabled, pass back spellcheck data from response
		if (this.searchConfig.spellchecker.enabled && data.spellcheck) {
			responseObj.spellcheck = data.spellcheck;
			this.searchConfig.debug && console.info('spellcheck: ', responseObj.spellcheck);
		}

		return responseObj;
	}

	// Formula for figuring out a highlighted descriptions weight
	_getDescriptionWeight(description) {
		var score = 0;
		description = utils.cleanString(description);
		if (!description) {
			return 0;
		}
		let bolds = description.match(/(<\/?b>)/g);
		let caps = description.match(/([A-Z][A-Za-z]+)/g);
		let htmlCheck = description.match(/(\=|\%\d+)/g);
		let maxCaps = this.searchConfig.descriptions.maxCapitalized;
		if (bolds && bolds.length) {
			score += bolds.length;
		}
		if (0 < maxCaps && caps && maxCaps <= caps.length) {
			score = -1;
		}
		if (htmlCheck && 1 < htmlCheck.length) {
			score -= 3;
		}
		return score;
	}

	_getTitle(result) {
		let title = '';
		if (result.title) {
			return result.title;
		}
		let url = result.url;
		let parts = url
			.replace(/\.\w+$/, '')
			.replace(/^.*\/\/[^:\/]+\//, '')
			.match(/[^:\/]+/g);
		if (Array.isArray(parts)) {
			parts.reverse();
			for (let index in parts) {
				let part = utils.titleCase(utils.cleanString(parts[index]).replace(/[-_]+/g, ' '));
				if (!part || 'Index' === part) {
					continue;
				}
				title = part;
				break;
			}
		}
		return title;
	}

	_getCache(key) {
		return cache.queries[key];
	}

	_setCache(key, value) {
		// limit how many are saved
		let keys = utils.objectKeys(cache.queries);
		if (CACHE_LIMIT < keys.length) {
			keys = keys.slice(0, 0 - CACHE_LIMIT - 1);
			keys.forEach((oldKey) => {
				delete cache.queries[oldKey];
			});
		}
		cache.queries[key] = value;
	}
}

export default Solr;
